import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import SEOMeta from '@/components/base/SEOMeta';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { getComicById } from '@/mocks/comics';

const baseUrl = 'https://hanmandq.com';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const comic = getComicById(id || '');
  const [activeTab, setActiveTab] = useState<'info' | 'chapters'>('info');

  if (!comic) {
    return (
      <>
        <Navbar />
        <div className="pt-24 pb-20 text-center">
          <h1 className="text-2xl font-heading text-foreground-900">漫画未找到</h1>
          <Link to="/" className="mt-4 inline-block text-primary-600 hover:text-primary-700 text-sm whitespace-nowrap">
            返回首页
          </Link>
        </div>
        <Footer />
      </>
    );
  }

  const articleUrl = `${baseUrl}/article/${id}`;
  const publishedDate = `${new Date().getFullYear()}-01-15`;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: comic.title,
    description: comic.description,
    author: { '@type': 'Person', name: comic.author },
    datePublished: publishedDate,
    dateModified: comic.updateDate,
    image: comic.cover,
    url: articleUrl,
    publisher: { '@type': 'Organization', name: '韩漫大全', url: baseUrl },
    mainEntityOfPage: { '@type': 'WebPage', '@id': articleUrl },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, item: { '@id': baseUrl, name: '首页' } },
      { '@type': 'ListItem', position: 2, item: { '@id': `${baseUrl}/category/${comic.categorySlug}`, name: comic.category } },
      { '@type': 'ListItem', position: 3, item: { '@id': articleUrl, name: comic.title } },
    ],
  };

  return (
    <>
      <SEOMeta
        title={`${comic.title}-韩漫大全 | 在线阅读${comic.author}作品`}
        description={comic.description}
        keywords={`${comic.title},韩漫大全,韩国漫画,${comic.category},${comic.author},免费漫画在线阅读`}
        canonicalUrl={articleUrl}
        ogImage={comic.cover}
        ogType="article"
        publishedTime={publishedDate}
        modifiedTime={comic.updateDate}
        schemaData={[articleSchema, breadcrumbSchema]}
      />

      <Navbar />

      <main className="pt-20 pb-16">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: comic.category, href: `/category/${comic.categorySlug}` },
              { label: comic.title },
            ]}
          />

          <div className="mt-8">
            <div className="flex flex-col md:flex-row gap-8">
              <div className="w-full md:w-72 shrink-0">
                <div className="rounded-xl overflow-hidden bg-background-200 aspect-[3/4]">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-2">
                  {comic.title}
                </h1>
                <p className="text-sm text-foreground-500 mb-4">
                  作者：{comic.author}
                </p>

                <div className="flex flex-wrap items-center gap-3 mb-6">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                    comic.status === '连载中'
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-secondary-100 text-secondary-700'
                  }`}>
                    {comic.status}
                  </span>
                  <span className="text-sm text-foreground-600 flex items-center gap-1 whitespace-nowrap">
                    <i className="ri-star-fill text-accent-500"></i> {comic.rating} 分
                  </span>
                  <span className="text-sm text-foreground-600 whitespace-nowrap">
                    <i className="ri-eye-line mr-1"></i>{(comic.views / 10000).toFixed(0)}万 阅读
                  </span>
                  <span className="text-sm text-foreground-600 whitespace-nowrap">
                    <i className="ri-heart-line mr-1"></i>{(comic.favorites / 10000).toFixed(0)}万 收藏
                  </span>
                  <span className="text-sm text-foreground-600 whitespace-nowrap">
                    共 {comic.chapters} 话
                  </span>
                </div>

                <div className="flex flex-wrap gap-2 mb-6">
                  {comic.tags.map((tag) => (
                    <span
                      key={tag.id}
                      className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700 whitespace-nowrap"
                    >
                      {tag.name}
                    </span>
                  ))}
                </div>

                <p className="text-sm text-foreground-700 leading-relaxed mb-6">
                  {comic.longDescription}
                </p>

                <div className="flex items-center gap-3">
                  <button className="px-6 py-2.5 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 font-medium text-sm transition-colors whitespace-nowrap">
                    <i className="ri-play-line mr-1"></i>开始阅读
                  </button>
                  <button className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 hover:bg-secondary-100 text-foreground-600 hover:text-secondary-700 transition-colors">
                    <i className="ri-heart-line text-lg"></i>
                  </button>
                  <button className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 hover:bg-secondary-100 text-foreground-600 hover:text-secondary-700 transition-colors">
                    <i className="ri-bookmark-line text-lg"></i>
                  </button>
                  <button className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 hover:bg-secondary-100 text-foreground-600 hover:text-secondary-700 transition-colors">
                    <i className="ri-share-forward-line text-lg"></i>
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-10">
              <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 w-fit mb-6">
                <button
                  onClick={() => setActiveTab('info')}
                  className={`px-5 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                    activeTab === 'info' ? 'bg-background-50 text-foreground-900' : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  作品详情
                </button>
                <button
                  onClick={() => setActiveTab('chapters')}
                  className={`px-5 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                    activeTab === 'chapters' ? 'bg-background-50 text-foreground-900' : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  章节列表
                </button>
              </div>

              {activeTab === 'info' ? (
                <div className="bg-background-50 rounded-xl border border-background-200/70 p-6">
                  <h3 className="text-lg font-heading text-foreground-900 mb-4">作品详情</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-foreground-500">作者</span>
                      <p className="text-foreground-800 mt-1">{comic.author}</p>
                    </div>
                    <div>
                      <span className="text-foreground-500">分类</span>
                      <p className="text-foreground-800 mt-1">{comic.category}</p>
                    </div>
                    <div>
                      <span className="text-foreground-500">状态</span>
                      <p className="text-foreground-800 mt-1">{comic.status}</p>
                    </div>
                    <div>
                      <span className="text-foreground-500">总章节</span>
                      <p className="text-foreground-800 mt-1">{comic.chapters} 话</p>
                    </div>
                    <div>
                      <span className="text-foreground-500">评分</span>
                      <p className="text-foreground-800 mt-1">{comic.rating} 分</p>
                    </div>
                    <div>
                      <span className="text-foreground-500">阅读量</span>
                      <p className="text-foreground-800 mt-1">{(comic.views / 10000).toFixed(0)} 万</p>
                    </div>
                    <div>
                      <span className="text-foreground-500">收藏数</span>
                      <p className="text-foreground-800 mt-1">{(comic.favorites / 10000).toFixed(0)} 万</p>
                    </div>
                    <div>
                      <span className="text-foreground-500">最近更新</span>
                      <p className="text-foreground-800 mt-1">{comic.updateDate}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-background-50 rounded-xl border border-background-200/70 p-6">
                  <h3 className="text-lg font-heading text-foreground-900 mb-4">
                    章节列表（共 {comic.chapters} 话）
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                    {comic.chapters_list.map((ch) => (
                      <a
                        key={ch.id}
                        href="#"
                        className="flex items-center justify-between p-3 rounded-lg hover:bg-background-100 transition-colors border border-background-200/70"
                      >
                        <span className="text-sm text-foreground-700">{ch.title}</span>
                        <span className="text-xs text-foreground-400 whitespace-nowrap">{ch.updateDate}</span>
                      </a>
                    ))}
                  </div>
                  {comic.chapters > 20 && (
                    <p className="text-center text-sm text-foreground-400 mt-4">
                      还有 {comic.chapters - 20} 个章节...
                    </p>
                  )}
                </div>
              )}
            </div>

            {comic.relatedComics.length > 0 && (
              <div className="mt-14">
                <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-6">
                  相关推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4 md:gap-5">
                  {comic.relatedComics.map((c) => (
                    <ComicCard key={c.id} comic={c} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}